import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CalendarDays, Pencil, Send, Check, MessageSquare, Ban, CalendarClock, Archive, Accessibility, X, UsersRound, ListOrdered, MapPin, BarChart3, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Info, History, ActionButton, ActionDialog, RunButton, BackLink, useLoad, fmt, errText, label,
  PageView, PageButtons, Tabbar, TabPanel, useTabs,
} from "@/components/control/kit";
import { Notice } from "@/components/control/v3";
import { SeatPanel, CapacityConflict } from "@/components/control/SeatPanel";
import { ActivityStepper, SeatsStep, PreviewStep, ACCESS_RADIO, AccessBadge } from "@/components/control/ActivityPages";
import { activityFields, activityBody, categoryLabel } from "@/components/control/activityFields";

function GroupLinkButton({ a, group, onDone }) {
  const [open, setOpen] = useState(false);
  const st = useLoad(open ? "/manage/groups" : null);
  const options = [{ value: "", label: "Keinem Kreis zuordnen" },
    ...(st.data?.groups || []).filter((g) => g.status === "aktiv").map((g) => ({ value: g.id, label: g.name_de }))];
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="link-activity-group" className="gap-1.5">
        <UsersRound className="h-4 w-4" aria-hidden="true" /> Kreis zuordnen
      </Button>
      <ActionDialog open={open && !!st.data} onOpenChange={setOpen} title="Aktivität einem Kreis zuordnen" testId="link-activity-group-dialog"
        description="Die Aktivität erscheint als Treffen im Kreis. Wer die Aktivität in der Mitglieder-App sieht, ändert sich dadurch nicht."
        fields={[
          { name: "group_id", label: "Kreis", type: "select", options, default: group?.id || "", placeholder: false },
          { name: "reason", label: "Grund", type: "textarea", required: true },
        ]}
        submitLabel="Speichern" onDone={onDone}
        onSubmit={async (v) => { await api.put(`/manage/activities/${a.id}/group`, { group_id: v.group_id || null, reason: v.reason }); return "Kreiszuordnung gespeichert."; }} />
    </>
  );
}

function AccessSection({ a, features, canManage, reload }) {
  const current = a.access?.features || {};
  const navigate = useNavigate();
  const draft = a.status === "entwurf";
  const today = new Date().toLocaleDateString("de-CH");
  return (
    <Section title={<span className="flex items-center gap-2"><Accessibility className="h-5 w-5 text-brand-magenta" /> Zugangsangaben</span>}
      testId="activity-access" actions={canManage && !["abgesagt", "archiviert"].includes(a.status) && (
        <ActionButton label="Zugang bearbeiten" icon={Pencil} testId="edit-access" onDone={reload} submitLabel={draft ? "Weiter zu Plätzen" : "Angaben speichern"}
          page="zugang" title={draft ? "Aktivität anlegen: Zugang" : "Zugangsangaben prüfen"} pageSubtitle="Prüfe jedes Merkmal, ohne unbekannte Angaben als vorhanden auszugeben."
          pageTop={draft && <ActivityStepper id={a.id} current="zugang" />} pageBack={draft ? `/aktivitaeten/${a.id}/bearbeiten` : undefined}
          cancelLabel={draft ? "Zurück" : "Abbrechen"} pageSubject={a.title_de}
          pageNotice={{ title: "Unbekannt bleibt offen", text: "Eine ungeklärte Angabe ist keine Bestätigung der Zugänglichkeit." }}
          description="Nur geprüfte Angaben eintragen. „Unbekannt“ bleibt als offene Aufgabe sichtbar."
          fields={[
            ...Object.entries(features).map(([key, text]) => ({ name: key, label: text, type: "radio", options: ACCESS_RADIO,
              default: current[key]?.value || "unbekannt", badge: (v) => <AccessBadge value={v} testId={`access-badge-${key}`} /> })),
            { name: "directions", label: "Wegbeschreibung und Hinweise", type: "textarea", default: a.access?.directions || "" },
            { name: "source", label: "Quelle der Angaben", required: true, help: "z. B. Vor-Ort-Prüfung am 12.10. oder Angabe der Organisation" },
            { type: "info", text: `Stand: ${today}` },
          ]}
          onSubmit={async ({ directions, source, ...rest }) => {
            await api.put(`/manage/activities/${a.id}/access`, { features: rest, directions, source });
            if (draft) navigate(`/aktivitaeten/${a.id}/plaetze`, { replace: true });
            return "Zugangsangaben gespeichert.";
          }} />
      )}>
      {!a.access ? <Empty text="Für diese Aktivität wurden noch keine Zugangsangaben erfasst." /> : (
        <>
          <ul className="divide-y divide-border">
            {Object.entries(features).map(([key, text]) => (
              <li key={key} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`access-feature-${key}`}>
                <span className="text-sm">{text}</span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {current[key]?.source && `Quelle: ${current[key].source} · ${fmt(current[key].updated_at)}`}
                  <Status value={current[key]?.value || "unbekannt"} />
                </span>
              </li>
            ))}
          </ul>
          {a.access.directions && <p className="mt-3 text-sm text-zinc-700">Hinweise: {a.access.directions}</p>}
          <p className="mt-2 text-xs text-muted-foreground">Offene Angaben: {a.access.open_count}
            {a.access.open_count > 0 && <> · <Link to={`/aktivitaeten/zugangsluecken/${a.id}`} data-testid="open-access-gap" className="font-semibold text-brand-purple underline">Fehlende Angabe klären</Link></>}</p>
        </>
      )}
    </Section>
  );
}

const decide = (a, p, action, extra) => api.post(`/manage/activities/${a.id}/participants/${p.id}`, { action, ...extra });

function CompanionActions({ a, p, reload }) {
  if (p.status !== "bestätigt" || !p.companions_requested || !["angefragt", "warteliste"].includes(p.companion_status)) return null;
  return (
    <>
      <RunButton label="Begleitplatz bestätigen" icon={Check} testId={`accept-companion-${p.id}`} success="Begleitplatz bestätigt." onDone={reload}
        run={() => decide(a, p, "accept_companion")} />
      {p.companion_status === "angefragt" && <RunButton label="Begleitplatz Warteliste" icon={ListOrdered} testId={`waitlist-companion-${p.id}`}
        success="Begleitplatz auf der Warteliste." onDone={reload} run={() => decide(a, p, "waitlist_companion")} />}
      <RunButton label="Begleitplatz ablehnen" icon={X} testId={`decline-companion-${p.id}`} success="Begleitplatz abgelehnt." onDone={reload}
        run={() => decide(a, p, "decline_companion")} />
    </>
  );
}

function ParticipantActions({ a, p, reload, onConflict }) {
  const accept = async () => {
    try {
      await decide(a, p, "accept");
      toast.success("Teilnahme bestätigt.");
      reload();
    } catch (err) {
      const msg = errText(err);
      if (err.response?.status === 409 && /Plätze|Kapazität/.test(msg)) onConflict(p.member_name);
      else toast.error(msg);
    }
  };
  const open = ["angefragt", "warteliste"].includes(p.status);
  return (
    <>
      {open && <Button size="sm" variant="outline" onClick={accept} data-testid={`accept-participation-${p.id}`} className="gap-1.5"><Check className="h-4 w-4" aria-hidden="true" />Bestätigen</Button>}
      {p.status === "angefragt" && <RunButton label="Warteliste" icon={ListOrdered} testId={`waitlist-participation-${p.id}`} success="Auf die Warteliste gesetzt."
        onDone={reload} run={() => decide(a, p, "waitlist")} />}
      {open && <ActionButton label="Ablehnen" icon={X} testId={`decline-participation-${p.id}`} onDone={reload} submitLabel="Ablehnen"
        fields={[{ name: "message", label: "Nachricht an das Mitglied", type: "textarea" }]}
        onSubmit={async (v) => { await decide(a, p, "decline", v); return "Anfrage abgelehnt."; }} />}
      <CompanionActions a={a} p={p} reload={reload} />
    </>
  );
}

function StatBox({ icon: Icon, value, text, testId }) {
  return (
    <div className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3" data-testid={testId}>
      <p className="flex items-center gap-2 text-2xl font-bold text-brand-purple"><Icon className="h-5 w-5 text-brand-magenta" aria-hidden="true" />{value}</p>
      <p className="mt-1 text-xs font-semibold text-zinc-600">{text}</p>
    </div>
  );
}

// C50: requests, confirmed and waitlist on their own page; decisions reuse the existing participant endpoints.
function ParticipationPage({ d, reload }) {
  const a = d.activity;
  const s = d.summary;
  const [tab, setTab] = useState("angefragt");
  const [conflict, setConflict] = useState(null);
  const tabs = useTabs([{ value: "angefragt", label: "Anfragen", count: s.requested }, { value: "bestätigt", label: "Bestätigt", count: s.confirmed },
    { value: "warteliste", label: "Warteliste", count: s.waitlist + (s.offered || 0) }], tab);
  // 2.10: open offers stay on the waitlist tab, read-only here; only the member's acceptance confirms them.
  const rows = d.participation.filter((p) => (p.status || "bestätigt") === tabs.value || (tabs.value === "warteliste" && p.status === "angeboten"));
  return (
    <PageView page="teilnahmen" label="Teilnahmeanfragen verwalten" icon={ListOrdered} testId="manage-participations"
      title="Teilnahmeanfragen verwalten" subtitle="Offene Anfragen sind noch keine bestätigten Plätze." subject={a.title_de}>
      {({ back }) => (
        <>
          {conflict && <CapacityConflict d={d} name={conflict} onReload={() => { setConflict(null); reload(); }} onClose={() => setConflict(null)} />}
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <StatBox icon={CalendarDays} value={s.confirmed} text="Bestätigt" testId="pp-confirmed" />
            <StatBox icon={UsersRound} value={s.requested} text="Anfragen offen" testId="pp-requested" />
            <StatBox icon={MapPin} value={s.capacity ? s.free : "–"} text="Freie Teilnehmendenplätze" testId="pp-free" />
            <StatBox icon={BarChart3} value={`${s.companions_confirmed} von ${s.companion_capacity || 0}`} text="Begleitplätze belegt" testId="pp-companions" />
          </div>
          <Tabbar tabs={tabs} onChange={setTab} testId="pp-tabs" ariaLabel="Teilnahmestatus" />
          <TabPanel tabs={tabs} testId="pp-panel">
            {rows.length === 0 ? <Empty text="Keine Einträge in diesem Status." testId="pp-empty" /> : (
              <ul className="space-y-2">
                {rows.map((p) => (
                  <li key={p.id} className="flex flex-col gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 lg:flex-row lg:items-center lg:justify-between" data-testid={`pp-row-${p.id}`}>
                    <div>
                      <p className="font-bold text-brand-purple">{p.member_name}</p>
                      <p className="text-sm text-zinc-600">Teilnahme {label(p.status || "bestätigt").toLowerCase()} · {p.companions_requested ? `Begleitplatz ${label(p.companion_status).toLowerCase()} (+${p.companions_requested})` : "Keine Begleitung"}</p>
                    </div>
                    {d.can.manage && <div className="flex flex-wrap gap-2"><ParticipantActions a={a} p={p} reload={reload} onConflict={setConflict} /></div>}
                  </li>
                ))}
              </ul>
            )}
          </TabPanel>
          <Notice icon={Lock} title="Private Angaben" text="Eine Teilnahmebestätigung öffnet keine privaten Passfelder. Dafür ist eine gesonderte persönliche Freigabe erforderlich." testId="pp-private" />
          <PageButtons back={back} secondary="Zurück zur Aktivität" testId="manage-participations" />
        </>
      )}
    </PageView>
  );
}

function Participants({ d, reload }) {
  const a = d.activity;
  const [conflict, setConflict] = useState(null);
  return (
    <Section title="Teilnahmeanfragen" testId="activity-participants" actions={<ParticipationPage d={d} reload={reload} />}>
      {conflict && <CapacityConflict d={d} name={conflict} onReload={() => { setConflict(null); reload(); }} onClose={() => setConflict(null)} />}
      <p className="mb-3 text-sm text-muted-foreground" data-testid="participation-summary">
        Bestätigt: {d.summary.confirmed} · Angefragt: {d.summary.requested} · Warteliste: {d.summary.waitlist} · Kapazität: {d.summary.capacity ?? "nicht festgelegt"}
        {d.summary.capacity ? ` · Frei: ${d.summary.free}` : ""}
      </p>
      {d.participation.length === 0 ? <Empty text="Noch keine Teilnahmen oder Anfragen." /> : (
        <ul className="divide-y divide-border">
          {d.participation.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`participation-${p.id}`}>
              <div><p className="font-medium text-brand-purple">{p.member_name}</p><p className="text-xs text-muted-foreground">{fmt(p.at)}</p>
                {p.companions_requested > 0 && <p className="text-xs text-zinc-700" data-testid={`participation-companions-${p.id}`}>
                  +{p.companions_requested} Begleitperson(en) · Begleitplatz: {label(p.companion_status)}</p>}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Status value={p.status} />
                {d.can.manage && <ParticipantActions a={a} p={p} reload={reload} onConflict={setConflict} />}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Actions({ d, reload }) {
  const a = d.activity;
  const base = `/manage/activities/${a.id}`;
  const navigate = useNavigate();
  const past = a.date && a.date < new Date().toISOString();
  return (
    <>
      <Status value={a.status} testId="activity-status" />
      <PreviewStep d={d} features={d.features} reload={reload} />
      {d.can.manage && !["abgesagt", "archiviert"].includes(a.status) && <GroupLinkButton a={a} group={d.group} onDone={reload} />}
      {a.status === "entwurf" && d.can.manage && (
        <>
          <ActionButton label="Bearbeiten" icon={Pencil} testId="edit-activity" fields={activityFields(a)} onDone={reload} submitLabel="Weiter zu Zugang"
            page="bearbeiten" title="Aktivität anlegen: Details" pageSubtitle="Erstelle einen Entwurf mit Termin, öffentlichem Ort und klaren Kosten." pageSubject={a.title_de}
            pageTop={<ActivityStepper id={a.id} current="bearbeiten" />}
            onSubmit={async (v) => {
              await api.patch(base, activityBody(v));
              navigate(`/aktivitaeten/${a.id}/zugang`, { replace: true });
              return "Entwurf gespeichert.";
            }} />
          <RunButton label="Zur Prüfung senden" icon={Send} variant="default" testId="submit-activity" success="Zur Prüfung gesendet." onDone={reload}
            run={() => api.post(`${base}/submit`)} />
        </>
      )}
      {a.status === "zur_pruefung" && d.can.approve && (
        <>
          <ActionButton label="Veröffentlichen" icon={Check} variant="default" testId="publish-activity" onDone={reload} title="Aktivität veröffentlichen bestätigen"
            page="veroeffentlichen" pageSubtitle="Eine Veröffentlichung erfolgt erst nach deiner bewussten Bestätigung."
            pageRows={[["Aktivität", a.title_de], ["Sichtbarkeit", "Mitglieder"]]} pageSubject={a.title_de}
            pageNotice={{ title: "Wirkung nach Bestätigung", text: "Der Vorgang bleibt bis zu deiner ausdrücklichen Bestätigung offen.", tone: "lavender" }}
            submitLabel="Jetzt veröffentlichen" description="Die Aktivität wird sofort in der Mitglieder-App sichtbar."
            fields={[{ name: "confirm", label: "Ich habe Details, Zeit und Zugangsangaben in der Vorschau geprüft.", type: "checkbox", required: true }]}
            onSubmit={async () => { await api.post(`${base}/review`, { action: "approve", version: a.version ?? 0 }); return "Aktivität veröffentlicht."; }} />
          <ActionButton label="Rückfrage" icon={MessageSquare} testId="ask-activity" onDone={reload} submitLabel="Zurück an Entwurf"
            page="rueckfrage" title="Rückfrage zur Aktivität" pageRows={[["Aktivität", a.title_de]]} pageSubject={a.title_de}
            fields={[{ name: "message", label: "Rückfrage", type: "textarea", required: true }]}
            onSubmit={async (v) => { await api.post(`${base}/review`, { action: "ask", version: a.version ?? 0, ...v }); return "Rückfrage gespeichert."; }} />
        </>
      )}
      {a.status === "zur_pruefung" && d.can.approve_blocked_reason && (
        <p className="w-full rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800" data-testid="approve-blocked-note">
          {d.can.approve_blocked_reason}
        </p>
      )}
      {a.status === "veröffentlicht" && d.can.manage && (
        <>
          <ActionButton label="Termin ändern" icon={CalendarClock} testId="reschedule-activity" onDone={reload} title="Termin oder Treffpunkt ändern"
            page="termin" pageSubtitle="Eine wesentliche Änderung wird nicht stillschweigend übernommen." pageSubject={a.title_de}
            pageRows={[["Aktivität", a.title_de], ["Bisher", `${(a.date_local || "").split("-").reverse().join(".")} · ${a.start}–${a.end}`], ["Bisheriger Treffpunkt", a.location],
              ["Rückmeldung", `${d.summary.confirmed + d.summary.requested} bestätigte oder angefragte Person(en) werden benachrichtigt`]]}
            submitLabel="Änderung speichern" description="Alle bestätigten und angefragten Teilnehmenden werden benachrichtigt."
            fields={[
              { name: "date", label: "Neues Datum", type: "date", required: true, default: a.date_local },
              { name: "start", label: "Beginn", type: "time", required: true, default: a.start },
              { name: "end", label: "Ende", type: "time", required: true, default: a.end },
              { name: "location", label: "Treffpunkt", default: a.location },
              { name: "reason", label: "Begründung", type: "textarea", required: true },
              { name: "message", label: "Nachricht an die Teilnehmenden", type: "textarea", required: true },
            ]}
            onSubmit={async (v) => { const { data } = await api.post(`${base}/reschedule`, v); return `Geändert. ${data.notified} Person(en) benachrichtigt.`; }} />
          <ActionButton label="Absagen" icon={Ban} danger testId="cancel-activity" onDone={reload} title="Aktivität absagen" submitLabel="Absagen"
            page="absagen" pageSubtitle="Prüfe Absagegrund, Auswirkungen und Nachricht vor der Bestätigung." pageSubject={a.title_de}
            pageRows={[["Aktivität", a.title_de], ["Betroffene", `${d.summary.confirmed} bestätigte Teilnahme(n) und ${d.summary.requested} offene Anfrage(n)`]]}
            pageNotice={{ title: "Entscheidung noch offen", text: "Begründung und Nachricht vor einer bestätigten Massnahme prüfen." }}
            description="Die Absage ist endgültig. Alle Teilnehmenden werden benachrichtigt."
            fields={[
              { name: "reason", label: "Grund", type: "textarea", required: true },
              { name: "message", label: "Nachricht an die Teilnehmenden", type: "textarea", required: true },
            ]}
            onSubmit={async (v) => { const { data } = await api.post(`${base}/cancel`, v); return `Abgesagt. ${data.notified} Person(en) benachrichtigt.`; }} />
        </>
      )}
      {d.can.manage && (["entwurf", "abgesagt"].includes(a.status) || (a.status === "veröffentlicht" && past)) && (
        <ActionButton label="Archivieren" icon={Archive} testId="archive-activity" onDone={reload} submitLabel="Archivierung bestätigen"
          page="archivieren" title="Aktivität archivieren" pageSubtitle="Eine Archivierung entfernt die Aktivität aus der aktiven Arbeitsliste." pageSubject={a.title_de}
          pageRows={[["Aktivität", a.title_de], ["Aktueller Status", label(a.status)], ["Verlauf", "Bleibt berechtigt nachvollziehbar"], ["Endgültige Löschung", "Wird durch Archivieren nicht ausgelöst"]]}
          pageNotice={{ title: "Deine Bestätigung ist erforderlich", text: "Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." }}
          description="Archivierte Aktivitäten sind in der App nicht mehr sichtbar."
          fields={[{ name: "reason", label: "Grund", type: "textarea", required: true }]}
          onSubmit={async (v) => { await api.post(`${base}/archive`, v); return "Aktivität archiviert."; }} />
      )}
    </>
  );
}

function ActivityView({ d, reload }) {
  const a = d.activity;
  return (
    <>
      <PageHeader title={a.title_de} subtitle={`${categoryLabel(a.category_key)} · ${fmt(a.date, a.timezone)}`} icon={CalendarDays} actions={<Actions d={d} reload={reload} />} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Vorschau wie in der Mitglieder-App" testId="activity-preview">
          <Info items={[["Termin", `${fmt(a.date, a.timezone)}${a.end ? ` bis ${a.end} Uhr` : ""}`], ["Zeitzone", a.timezone || "–"],
            ["Treffpunkt", a.location], ["Land", a.country], ["Kosten", a.cost_note], ["Gastgeber:in", a.host_name || a.organization_name],
            ["Teilnahme", a.requires_confirmation ? "nach Bestätigung" : "direkt"], ["Beschreibung", a.description_de],
            ["Organisation (nur im Control OS)", d.organization ? <Link key="o" to={`/organisationen/${d.organization.id}`} data-testid="activity-organization-link" className="text-brand-purple underline">{d.organization.name}</Link> : "keine"],
            ["Kreis (nur im Control OS)", d.group ? <Link key="g" to={`/kreise-gruppen/${d.group.id}`} data-testid="activity-group-link" className="text-brand-purple underline">{d.group.name}</Link> : "keinem Kreis zugeordnet"],
            a.review_note && ["Letzte Rückfrage", a.review_note], a.cancel_reason && ["Absagegrund", a.cancel_reason]]} />
        </Section>
        <AccessSection a={a} features={d.features} canManage={d.can.manage} reload={reload} />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Participants d={d} reload={reload} />
        <SeatPanel key={`${d.summary.capacity}-${d.summary.companion_capacity}`} d={d} reload={reload} />
        <SeatsStep key={`step-${d.summary.capacity}-${d.summary.companion_capacity}`} d={d} reload={reload} />
      </div>
      <div className="mt-5"><Section title="Verlauf" testId="activity-history"><History items={a.history} /></Section></div>
    </>
  );
}

export default function ActivityDetail() {
  const { id } = useParams();
  const st = useLoad(`/manage/activities/${id}`);
  return (
    <div>
      <BackLink to="/aktivitaeten">Zurück zu Aktivitäten</BackLink>
      <Loadable state={st}>{(d) => <ActivityView d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
